import {Button, type ButtonProps} from '@chakra-ui/button';
import type {As, ComponentWithAs} from '@chakra-ui/react';
import {chakra, forwardRef, Text, Tooltip} from '@chakra-ui/react';
import type {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faChevronRight} from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {dataAttr} from '@utils';
import {useRef, useState, type JSX, type RefObject} from 'react';
import useResizeObserver from 'use-resize-observer';

import {Icon} from '../Icon';

import {useStyles} from './context';
import {parts} from './theme/anatomy';

interface Props extends ButtonProps {
  icon?: IconDefinition | JSX.Element;
  label?: string | JSX.Element;
  isActive?: boolean;
  /** Used for text highlighting (true by default) */
  level?: 0 | 1;
  isExpended?: boolean;
  isCollapsible?: boolean;
}

const isIconDefinition = (icon: IconDefinition | JSX.Element): icon is IconDefinition => {
  return (icon as IconDefinition).iconName !== undefined;
};

export const NavMenuItem: ComponentWithAs<As, Props> = forwardRef<Props, As>((props, ref) => {
  const {icon, label, children, isExpended, isCollapsible = false, isActive, sx, level = 0, ...buttonProps} = props;
  const {[`menu-item`]: style, icon: iconStyle} = useStyles();
  const textRef = useRef<HTMLParagraphElement | null>(null);
  useResizeObserver({
    ref: textRef as RefObject<HTMLParagraphElement>,
    onResize: () => {
      if (textRef.current) {
        setIsTruncated(isTextTruncated(textRef.current));
      }
    },
  });

  const [isTruncated, setIsTruncated] = useState(false);

  const renderIcon = () => {
    if (!icon) return <EmptyIcon data-testid={`icon-placeholder`} />;
    if (isIconDefinition(icon))
      return <Icon fixedWidth sx={iconStyle} icon={icon as any} style={{marginTop: `-1px`}} />;
    return icon;
  };

  return (
    <Tooltip label={children || label} isDisabled={!isTruncated}>
      <Button
        {...{
          [`data-level`]: level,
          [`data-collapsible`]: dataAttr(isCollapsible),
          [`data-current`]: dataAttr(isActive),
          [`data-expended`]: dataAttr(isExpended),
        }}
        {...parts[`menu-item`].attrs}
        ref={ref}
        variant={`plain`}
        sx={{...style, ...sx, _focus: {boxShadow: `none`}}}
        leftIcon={renderIcon()}
        role={`menuitem`}
        {...buttonProps}>
        <Text ref={textRef} fontWeight={`inherit`} textOverflow={`ellipsis`} overflow={`hidden`}>
          {children || label}
        </Text>
        {isCollapsible && <ChevronIcon isOpen={!!isExpended} />}
      </Button>
    </Tooltip>
  );
});

export type {Props as SidebarButtonProps};

const EmptyIcon = chakra(`span`, {
  label: `EmptyIcon`,
  baseStyle: {
    width: 32,
    height: 32,
  },
});

export const ChevronIcon = (props: {isOpen: boolean}) => {
  return (
    <FontAwesomeIcon
      aria-hidden
      icon={faChevronRight}
      style={{
        marginLeft: `auto`,
        fontSize: `0.75em`,
        transform: props.isOpen ? `rotate(90deg)` : undefined,
        transition: `transform 0.3s`,
        transformOrigin: `center`,
      }}
    />
  );
};

const isTextTruncated = (element: HTMLElement) => {
  return element.scrollWidth > element.clientWidth;
};
